import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DateRange } from '@angular/material/datepicker';

// rxjs
import { BehaviorSubject, forkJoin } from 'rxjs';
import { filter, finalize, map, take } from 'rxjs/operators';

// Services
import { AnalyticsService } from 'src/app/services/analytics.service';
import { DialogService } from '../../services/dialog.service';
import { MyProfileService } from 'src/app/services/my-profile.service';
import { AuthService } from '../../auth/auth.service';
import {
  GetRelationContactsGQL,
  GetAccountDetailsGQL,
  GetAccountTermPolicyFormsGQL,
  GetPreferredLocationGQL,
} from '../../services/graphql.service';
import { LoadingIndicatorService } from '../../services/loading-indicator.service';
import { ReservationRequestService } from '../../services/reservation/reservation-request.service';

// Models
import { FranchiseLocation } from '../../models/franchise.model';
import { ReservationPetInfo } from '../../models/reservation-pet-info.model';
import { ReservationServiceCategory } from 'src/app/models/reservation/reservation-service-category.model';
import { ReservationRequestItem } from 'src/app/models/reservation/reservation-request.model';

// Components
import { ReservationLocationComponent } from '../../components/reservation/reservation-location/reservation-location.component';
import { ReservationDogsComponent } from '../../components/reservation/reservation-dogs/reservation-dogs.component';
import { ReservationServicesComponent } from '../../components/reservation/reservation-services/reservation-services.component';
import { ReservationDateComponent } from '../../components/reservation/reservation-date/reservation-date.component';
import { ReservationAddOnsComponent } from '../../components/reservation/reservation-add-ons/reservation-add-ons.component';
import { ConfirmDialogComponent } from '../../components/_share/confirm-dialog/confirm-dialog.component';
import { ReservationSummaryComponent } from 'src/app/components/reservation/reservation-summary/reservation-summary.component';
import { CanDeactivateComponent } from '../../models/can-deactivate-component.model';
import { MatTabChangeEvent } from '@angular/material/tabs';

import * as lodash from 'lodash';
import moment from 'moment';

const BOARDING_RESERVATION_MAX_DAYS = 14;

@Component({
  selector: 'app-interview-request-page',
  templateUrl: './reservation-page.component.html',
  styleUrls: ['./reservation-page.component.scss'],
})
export class ReservationPageComponent
  implements OnInit, CanDeactivateComponent
{
  @HostBinding('class') class = 'page-config';
  // #region -> Property - Component IO <-
  // ----------

  @ViewChild('location', { static: false })
  locationForm: ReservationLocationComponent;
  @ViewChild('dog', { static: false }) dogForm: ReservationDogsComponent;
  @ViewChild('service', { static: false })
  serviceForm: ReservationServicesComponent;
  @ViewChild('date', { static: false }) dateForm: ReservationDateComponent;
  @ViewChild('addOn', { static: false }) addOnForm: ReservationAddOnsComponent;
  @ViewChild('summary', { static: false })
  summaryForm: ReservationSummaryComponent;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property <-
  // ----------

  needToReloadDog$ = new BehaviorSubject<boolean>(false);
  useDateRange$ = new BehaviorSubject<boolean>(false);

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - Tabs <-
  // ----------

  currentTabIndex = 0;
  prevTabIndex = 0;
  enabledTabs = new Set<number>();

  // Check if having "boarding" service selected
  hasBoardingServiceSelected = false;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property - UI <-
  // ----------

  reservationRequestSubmitted = false;
  reservationRequestLocationName = '';

  // ----------
  // #endregion

  /////////////////////////////

  constructor(
    private _analytics: AnalyticsService,
    private dialogService: DialogService,
    private myProfileService: MyProfileService,
    private titleService: Title,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService,

    private reservationRequestService: ReservationRequestService,
    private loadingIndicatorService: LoadingIndicatorService,

    private getRelationContactsQuery: GetRelationContactsGQL,
    private getAccountDetailsQuery: GetAccountDetailsGQL,
    private getAccountTermPolicyFormsQuery: GetAccountTermPolicyFormsGQL,
    private getPreferredLocationQuery: GetPreferredLocationGQL
  ) {
    this.titleService.setTitle('Client Portal - Book a Reservation');
    this.enabledTabs.add(0);
  }

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    this._analytics.logScreen('reservationRequestPage');

    this.reservationRequestService.reset();
    this.myProfileService.fetchLocationList();

    // checks reservation requirements and sends user back if not met
    this._checkReservationRequirements();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - UI <-
  // ----------

  navigateToTabIndex(tabIndex: number, viaTabContent = false): void {
    //
    /*
      Set `prevTabIndex` the same as `currentTabIndex` to help bypass `onTabChanged()` event.
    */
    if (this.enabledTabs.has(tabIndex)) {
      this.currentTabIndex = tabIndex;
      this.prevTabIndex = this.currentTabIndex;

      this.reloadUIElementsIfNeeded(this.currentTabIndex);
      return;
    }

    if (viaTabContent) {
      this.currentTabIndex = tabIndex;
      this.prevTabIndex = this.currentTabIndex;
      this.enabledTabs.add(tabIndex);

      this.reloadUIElementsIfNeeded(this.currentTabIndex);
      return;
    }

    this.prevTabIndex = this.currentTabIndex;
    this.currentTabIndex = tabIndex;
    this.reloadUIElementsIfNeeded(this.currentTabIndex);
  }

  onTabChanged(event: MatTabChangeEvent) {
    //
    // Skip the processing if `navigateToTabIndex()` also occurs.
    if (this.prevTabIndex === event.index) {
      return;
    }

    switch (this.prevTabIndex) {
      case 0: // Location
        //
        this.locationForm.fetchData();
        break;
      case 1: // Dogs
        //
        this.dogForm.resetFormData(
          this.reservationRequestService.reservationRequest
        );
        break;
      case 2: // Services
        //
        this.serviceForm.resetFormData(
          this.reservationRequestService.reservationRequest
        );
        break;
      case 3: // Date
        //
        this.dateForm.resetData(
          this.reservationRequestService.reservationRequest
        );
        break;
      case 4: // Addon
        //
        this.addOnForm.resetFormData(
          this.reservationRequestService.reservationRequest
        );
        break;
      default:
        break;
    }

    // Check if needs to reload some UI elements when switching
    this.reloadUIElementsIfNeeded(event.index);

    // Update the record
    this.prevTabIndex = event.index;
  }

  reloadUIElementsIfNeeded(targetIndex: number) {
    //
    switch (targetIndex) {
      case 5: // Summary
        this.summaryForm.reloadUIElements();
        break;
      default:
        break;
    }
  }

  useDateRange(): boolean {
    //
    return this.reservationRequestService.serviceNeedsDateRange();
  }

  showSummary(): void {
    // Check if need to show "payment credit card" section
    if (this.reservationRequestService.hasBoardingServiceSelected()) {
      this.hasBoardingServiceSelected = true;
    } else {
      this.hasBoardingServiceSelected = false;
    }
    this.changeDetectorRef.markForCheck();

    //
    this.navigateToTabIndex(5, true);
  }

  canDeactivate() {
    //
    if (this.reservationRequestSubmitted) {
      return true;
    }
    // you cant get to the other pages without selecting a dog so this is the only check we need
    if (this.dogForm.isSaveEnabled) {
      return this.dialogService.confirmDiscard();
    }
    return true;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - UI - Steps <-
  // ----------

  continueWithSelectedLocation(selectedLocation: FranchiseLocation): void {
    // Check if the "selection" gets changed
    if (
      this.reservationRequestService.locationChangeWarning(selectedLocation)
    ) {
      // Show the warning
      this._showChangeWarning(
        () => {
          //
          this.setSelectedLocation(selectedLocation);
          this.navigateToTabIndex(1, true);
        },
        () => {
          // Reset the data and stay at the current page
          this.locationForm.fetchData();
        }
      );
    } else {
      // Go with the normal process.
      // Only update the selection if it is in "init" state
      if (!this.reservationRequestService.reservationRequest.selectedLocation) {
        this.setSelectedLocation(selectedLocation);
      }
      this.navigateToTabIndex(1, true);
    }
  }

  continueWithSelectedPets(selectedPets: ReservationPetInfo[]): void {
    // Check if the "selection" gets changed
    if (this.reservationRequestService.petChangeWarning(selectedPets)) {
      // Show the warning
      this._showChangeWarning(
        () => {
          //
          this.setSelectedPets(selectedPets);
          this.navigateToTabIndex(2, true);
        },
        () => {
          // Reset the data and stay at the current page
          this.dogForm.fetchData(
            this.reservationRequestService.reservationRequest
          );
        }
      );
    } else {
      // Go with the normal process.
      // Only update the selection if it is in "init" state
      if (
        this.reservationRequestService.reservationRequest.selectedPets
          .length === 0
      ) {
        this.setSelectedPets(selectedPets);
      }
      this.navigateToTabIndex(2, true);
    }
  }

  continueWithSelectedServices(
    newReservationRequest: ReservationRequestItem
  ): void {
    // Check if the "selection" gets changed
    if (
      this.reservationRequestService.serviceChangeWarning(newReservationRequest)
    ) {
      // Show the warning
      this._showChangeWarning(
        () => {
          // Confirm the change
          this.setSelectedServices(newReservationRequest);
          this.navigateToTabIndex(3, true);
        },
        () => {
          // Reset the data and stay at the current page
          this.serviceForm.resetFormData(
            this.reservationRequestService.reservationRequest
          );
        }
      );
    } else {
      // Go with the normal process.
      // Only update the selection if it is in "init" state
      if (
        !this.reservationRequestService.reservationRequest.hasCoreServices()
      ) {
        this.setSelectedServices(newReservationRequest);
      }
      this.navigateToTabIndex(3, true);
    }
  }

  continueWithSelectedDate(newDateRange: DateRange<any>): void {
    // Check if the "selection" gets changed
    if (this.reservationRequestService.dateChangeWarning(newDateRange)) {
      // Show the warning
      this._showChangeWarning(
        () => {
          // Confirm the change
          this.setSelectedDateRange(newDateRange);
        },
        () => {
          // Reset the data and stay at the current page
          this.dateForm.resetData(
            this.reservationRequestService.reservationRequest
          );
        }
      );
    } else {
      // Go with the normal process.
      // Only update the selection if it is in "init" state
      if (
        !this.reservationRequestService.reservationRequest.selectedDateRange
      ) {
        this.setSelectedDateRange(newDateRange);
      } else {
        this.navigateToTabIndex(4, true);
      }
    }
  }

  continueWithSelectedAddons(
    newReservationRequest: ReservationRequestItem
  ): void {
    // Check if the "selection" gets changed
    if (
      this.reservationRequestService.addonChangeWarning(newReservationRequest)
    ) {
      // Show the warning
      this._showChangeWarning(
        () => {
          // Confirm the change
          this.setSelectedAddons(newReservationRequest);
          // Next
          this.showSummary();
        },
        () => {
          // Reset the data and stay at the current page
          this.addOnForm.resetFormData(
            this.reservationRequestService.reservationRequest
          );
        }
      );
    } else {
      // Go with the normal process.
      // Only update the selection if it is in "init" state
      if (
        !this.reservationRequestService.reservationRequest.hasAddonServices()
      ) {
        this.setSelectedAddons(newReservationRequest);
      }
      // Next
      this.showSummary();
    }
  }

  // ----------
  // #endregion

  // ///////////////////////////

  // #region -> Method - UI - Save on Steps <-
  // ----------

  setSelectedLocation(location: FranchiseLocation): void {
    this._analytics.logButtonTap('reservationRequest_saveLocation');

    // Update to the "reservation request"
    this.reservationRequestService.updateSelectedLocation(location);

    // Start to fetch the "dog" data and "service" data
    this.needToReloadDog$.next(true);
    this.reservationRequestService
      .fetchAvailableServices(location.id)
      .subscribe();

    // Disable some tabs
    this.enabledTabs.delete(2);
    this.enabledTabs.delete(3);
    this.enabledTabs.delete(4);
    this.enabledTabs.delete(5);
  }

  setSelectedPets(pets: ReservationPetInfo[]): void {
    this._analytics.logButtonTap('reservationRequest_saveDogs');

    // Update to the "reservation request"
    this.reservationRequestService.updateSelectedPets(pets);

    // Disable some tabs
    this.enabledTabs.delete(3);
    this.enabledTabs.delete(4);
    this.enabledTabs.delete(5);
  }

  setSelectedServices(newReservationRequest: ReservationRequestItem): void {
    this._analytics.logButtonTap('reservationRequest_saveServices');

    // Update the request item directly
    this.reservationRequestService.updateSelectedServices(
      newReservationRequest
    );

    // Reset the selection of "dates" by checking the "use date range" flag
    this.useDateRange$.next(
      this.reservationRequestService.serviceNeedsDateRange()
    );

    // Disable some tabs
    this.enabledTabs.delete(4);
    this.enabledTabs.delete(5);
  }

  setSelectedDateRange(dateRange: DateRange<any>): void {
    this._analytics.logButtonTap('reservationRequest_saveDate');

    if (this._validateDateRange(dateRange)) {
      // Check the conflict and update if it does not.
      this._checkDateConflict(dateRange);
    }
  }

  setSelectedAddons(newReservationRequest: ReservationRequestItem): void {
    this._analytics.logButtonTap('reservationRequest_saveAddOns');

    // Update the request item directly
    this.reservationRequestService.updateReservationRequestItem(
      newReservationRequest
    );
  }

  submitReservation(): void {
    this._analytics.logButtonTap('reservationRequest_submit');

    // Submit it
    this._submitReservationRequest();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method - Steps <-
  // ----------

  private _showChangeWarning(
    changeCallback: () => void,
    cancelCallBack: () => void
  ) {
    //
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'general-dialog',
      data: {
        title: 'Are you sure you want to make changes to the reservation?',
        content:
          'Making a change may reset the rest parts of the reservation request.',
        okText: 'Change',
        okFn: () => {
          dialogRef.close();
          changeCallback();
        },
        cancelText: 'Cancel',
        cancelFn: () => {
          dialogRef.close();
          cancelCallBack();
        },
      },
      autoFocus: false,
      disableClose: false,
    });
  }

  // ----------
  // #endregion

  // ///////////////////////////

  // #region -> Private Method - Date <-
  // ----------

  private _validateDateRange(dateRange: DateRange<any>): boolean {
    /*
      If the "date" is a "range", it cannot be more than 21 days.
    */
    if (dateRange.end) {
      const diff =
        moment(dateRange.end).diff(moment(dateRange.start), 'days') + 1;
      if (diff > BOARDING_RESERVATION_MAX_DAYS) {
        this._showAlert(
          'Please Contact Camp',
          `To make a reservation longer than ${BOARDING_RESERVATION_MAX_DAYS} days, please contact your Camp location.`
        );
        return false;
      }
    }

    return true;
  }

  private _checkDateConflict(dateRange: DateRange<any>): void {
    //
    this.loadingIndicatorService.show();

    //
    this.reservationRequestService
      .checkConflict(dateRange)
      .pipe(finalize(() => this.loadingIndicatorService.hide()))
      .subscribe((petBookingMatrix) => {
        if (
          petBookingMatrix.some((petBookingList) => petBookingList.length > 0)
        ) {
          this.enabledTabs.delete(4);
          this.enabledTabs.delete(5);
          //
          this._showAlert(
            'Reservation Conflict',
            'One or more selected dates conflict with another confirmed reservation. Please visit your Upcoming Reservations to review.'
          );
        } else {
          // Save the date range and proceed
          this.reservationRequestService.updateSelectedDateRange(dateRange);
          this.navigateToTabIndex(4, true);
        }
      });
  }

  private _showAlert(title, content) {
    //
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'general-dialog',
      data: {
        title,
        content,
        okText: 'OK',
        okFn: () => {
          dialogRef.close();
        },
      },
      autoFocus: false,
      disableClose: true,
    });
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  // check the reservation requirements
  // if requirements are not met, call generateAlert() to display alert and send back
  private _checkReservationRequirements(): void {
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        forkJoin([
          // Retrieve account details
          this.getAccountDetailsQuery
            .fetch({ accountId })
            .pipe(map((res) => res.data.getAccount)),

          // Fetch the preferred location
          this.getPreferredLocationQuery
            .fetch({ accountId })
            .pipe(map((res) => res.data.getPreferredLocation)),

          // Retrieve other contacts
          this.getRelationContactsQuery
            .fetch({ accountId })
            .pipe(map((res) => res.data.getRelationContacts)),

          // Retrieve camper application forms
          this.getAccountTermPolicyFormsQuery
            .fetch({ accountId })
            .pipe(map((res) => res.data.getAccountTermPolicyForms)),
        ]).subscribe(
          ([
            account,
            preferredFranchise,
            relationContacts,
            accountTermPolicyForms,
          ]) => {
            const { city, address1, country } = account;
            // check to see if a home address has been set
            const isHomeAddressValid = !!city && !!address1 && !!country;
            // check to see if at least one other contact has been set
            const isOtherContactsValid = relationContacts.some(
              (contact) => !!contact.isEmergencyContact && !!contact.id
            );
            // check to see if the completed camper application matches the user account's preferred location
            const isCamperApplicationValid = accountTermPolicyForms.some(
              (form) =>
                form.termPolicyForm.geoCountry.countryIso.toLowerCase() ===
                preferredFranchise.country
            );

            if (
              !(
                isHomeAddressValid &&
                isOtherContactsValid &&
                isCamperApplicationValid
              )
            ) {
              this._generateAlert(
                isHomeAddressValid,
                isOtherContactsValid,
                isCamperApplicationValid
              );
            }
          }
        );
      });
  }

  // generates the alert text if all requirements to make a reservation are not met
  private _generateAlert(
    isHomeAddressValid: boolean,
    isOtherContactsValid: boolean,
    isCamperApplicationValid: boolean
  ): void {
    const missingInfoList = [];

    if (!isHomeAddressValid) {
      missingInfoList.push('<br>- Home Address');
    }
    if (!isOtherContactsValid) {
      missingInfoList.push('<br>- At Least 1 Emergency Contact');
    }
    if (!isCamperApplicationValid) {
      missingInfoList.push('<br>- Signed Terms & Policies');
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'general-dialog',
      data: {
        title: 'Missing Information',
        content: `The following info is required in order to make a reservation: ${missingInfoList.join(
          ''
        )} <br><br>Please go to "My Profile" to fill it out.`,
        okText: 'OK',
        okFn: () => {
          dialogRef.close();
          this.router.navigate(['/my-profile']);
        },
      },
      autoFocus: false,
      disableClose: true,
    });
  }

  private _submitReservationRequest(): void {
    //
    this.loadingIndicatorService.show();

    //
    this.reservationRequestService
      .submitReservationRequest()
      .pipe(finalize(() => this.loadingIndicatorService.hide()))
      .subscribe((request) => {
        if (request) {
          // Keep the location name before the reset
          this.reservationRequestLocationName =
            this.reservationRequestService.reservationRequest.selectedLocation.name;
          this.reservationRequestSubmitted = true;
          this.reservationRequestService.reset();
        }
      });
  }

  // ----------
  // #endregion

  /////////////////////////////
}
